import type { Breadcrumb, Event } from '@sentry/types';

export const SENSITIVE_URL_PARAMS = [
  'session_code',
  'execution',
  'tab_id',
  'code',
  'state',
  'nonce',
];

const SENSITIVE_QUERY_PARAM_REGEX = new RegExp(
  `([?&](?:${SENSITIVE_URL_PARAMS.join('|')})=)[^&#]*`,
  'g',
);

const URL_DATA_KEYS = ['url', 'from', 'to', 'http.url'];

/**
 * Drops the URL fragment and redacts sensitive query parameter values. The rest of the string is
 * kept as is, so it is safe for absolute URLs, relative URLs and transaction names.
 */
export function redactUrlSecrets(value: string): string {
  const fragmentStart = value.indexOf('#');
  const withoutFragment = fragmentStart === -1 ? value : value.slice(0, fragmentStart);

  return withoutFragment.replace(SENSITIVE_QUERY_PARAM_REGEX, '$1REDACTED');
}

/**
 * Removes URL fragments (for example the Slack embed `#code=`) and sensitive query parameters from
 * every place where Sentry records the page or request URL.
 */
export function redactEventUrls<T extends Event>(event: T): T {
  if (event.request?.url) {
    event.request.url = redactUrlSecrets(event.request.url);
  }

  if (event.transaction) {
    event.transaction = redactUrlSecrets(event.transaction);
  }

  event.breadcrumbs?.forEach(redactBreadcrumbUrls);

  event.spans?.forEach((span) => {
    if (span.description) {
      span.description = redactUrlSecrets(span.description);
    }

    redactUrlData(span.data);
  });

  event.exception?.values?.forEach((exception) => {
    exception.stacktrace?.frames?.forEach((frame) => {
      if (frame.filename) {
        frame.filename = redactUrlSecrets(frame.filename);
      }

      if (frame.abs_path) {
        frame.abs_path = redactUrlSecrets(frame.abs_path);
      }
    });
  });

  return event;
}

function redactBreadcrumbUrls(breadcrumb: Breadcrumb): Breadcrumb {
  redactUrlData(breadcrumb.data);

  return breadcrumb;
}

function redactUrlData(data: Record<string, unknown> | undefined): void {
  if (!data) {
    return;
  }

  for (const key of URL_DATA_KEYS) {
    const value = data[key];

    if (typeof value === 'string') {
      data[key] = redactUrlSecrets(value);
    }
  }
}
